import qs from 'qs';
import uniq from 'lodash.uniq';
import { useState, useEffect } from 'react';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import DocumentSigner from '@castandcrew/react-document-signer/DocumentSigner';
import Typography from 'components/CncUI/Typography';
import Loader from 'components/Loader';
import SubmitModal from './modals/Submit';

import useStates from './hooks/useStates';
import useSubmitW4 from './hooks/useSubmitW4';
import useW4 from './hooks/useW4';
import useGetW4ImageUrls from './hooks/useGetW4ImageUrls';
import useCurrentUserProfile from './hooks/useCurrentUserProfile';
import useError from './hooks/useError';
import useCountry from 'hooks/useCountry';

import history from 'constants/config/history';

import Logger from 'utilities/logger';

import 'react-datepicker/dist/react-datepicker.css';

const styles = ({ breakpoints }) => ({
  root: {
    // TODO:
    // This next line is needed because MyC&C does not have a sensible CSS reset.
    // Somebody should really add one so we can get rid of this.
    // Alternatively, we should include the CSS reset in react-document-signer.
    '& *, *:before, *:after': {
      boxSizing: 'border-box',
    },
  },
  header: {
    marginTop: 40,
    marginBottom: 0,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  signerContainer: {
    display: 'grid',
    gridTemplateColumns: ' 100%',
    gridTemplateRows: ' 100%',
    boxSizing: ' border-box',
    width: ' 100%',
    height: ' calc(100% - 130px)',
  },
  signer: {},
});

const NewW4 = ({ classes, location: { search, pathname } }) => {
  const { id, state: stateId } = qs.parse(search.slice(1));
  const { country: mainCountry, setCountry } = useCountry();
  const country = pathname.startsWith('/td1s/new/') ? 'CAN' : 'US';
  const isCAN = country === 'CAN';
  const title = isCAN ? 'TD1' : 'W-4';

  const onError = useError(
    `There was an error displaying your new ${title}. Please try again later or
    contact product support if the issue persists.`,
  );
  const { loading, data: profile } = useCurrentUserProfile({
    variables: { country },
    onError,
  });
  const { loading: w4Loading, data: w4 } = useW4(id, { onError });
  const getW4ImageUrls = useGetW4ImageUrls({ onError });
  const { data: states, loading: loadingStates } = useStates({
    variables: { country },
    onError,
  });
  const [submitW4] = useSubmitW4();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [submittedW4, setSubmittedW4] = useState();

  useEffect(() => {
    if (!loading && !w4Loading && !loadingStates) {
      const errors = [];
      if (!w4) {
        errors.push('Document service is down');
      }
      if (!profile) {
        errors.push('Profile service is down');
      }
      if (!states) {
        return null;
      }
      if (errors.length > 0) {
        onError(`There was an error displaying your new ${title}. ${errors.join(
          ', ',
        )}. Please try again later or contact product support if the issue
        persists.
      `);
      }
    }
  }, [loading, profile, w4Loading, w4, loadingStates, states]);

  if (mainCountry === 'US' && pathname.startsWith('/td1s/new/')) {
    setCountry('CAN');
  }

  if (w4Loading || loading || loadingStates) return <Loader />;
  if (!w4 || !profile || !states) return null;

  // Makes a few changes to the w4 data to conform to the DocumentSigner API:
  // 1. Convert field options from ['foo', 'bar', 'baz']
  // to { foo: 'foo', bar: 'bar', baz: 'baz' }.
  // 2. Give radio group items the fieldType RDO.
  // 3. Add radioGroupPages attribute to radio fields.
  // 4. Ensure every rule's conditionalRuleIds field always contains an array.
  const documentSignerDocument = w4 && {
    ...w4,
    fields: w4.fields.map(field => ({
      ...field,
      options:
        field.options &&
        field.options.reduce((optionsObj, option) => {
          optionsObj[option] = option;
          return optionsObj;
        }, {}),
      radioGroupItems:
        field.radioGroupItems &&
        field.radioGroupItems.map(radioGroupItem => ({
          ...radioGroupItem,
          fieldType: 'RDO',
        })),
      radioGroupPages:
        field.radioGroupItems &&
        uniq(field.radioGroupItems.map(({ page }) => page)),
    })),
    rules: w4.rules.map(rule => ({
      ...rule,
      conditionalRuleIds: rule.conditionalRuleIds || [],
    })),
  };

  // Construct actor object for DocumentSigner
  const { firstName, lastName } = profile.basicInfo;
  const names = [firstName, lastName].filter(x => x);
  const actor = {
    name: names.join(' '),
    initials: names.map(name => name[0]).join(''),
  };

  const w4TypeDescription = (() => {
    if (!stateId) return 'Federal';
    const label = isCAN ? 'Province' : 'State';
    const stateName = states.find(({ id }) => id === stateId).name;
    return `${stateName} ${label}`;
  })();

  const startSubmission = doc => {
    setSubmittedW4(doc);
    setIsConfirmationOpen(true);
  };

  const redirectToMyW4s = () => history.push(isCAN ? '/td1s' : '/w4s');

  const confirmSubmission = name => {
    const fields = submittedW4.fields.map(({ id, value }) => ({ id, value }));
    return submitW4({ fields, id, name }).then(redirectToMyW4s);
  };

  const params = { type: 'crew' };
  return (
    <Grid container spacing={2} id="NewW4" className={classes.root}>
      <Grid item xs={12} className={classes.header}>
        <Typography variant="h2" id="NewW4Header">
          Adding New {w4TypeDescription} {title}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.signerContainer}>
          <DocumentSigner
            params={params}
            className={classes.signer}
            documents={[documentSignerDocument]}
            actor={actor}
            getDocumentImageUrls={({ documentId, page }) =>
              getW4ImageUrls(documentId, page)
            }
            actions={{
              submit: {
                text: 'Submit',
                action: docs => startSubmission(docs[0]),
              },
              cancel: {
                text: 'Cancel',
                action: redirectToMyW4s,
              },
            }}
            throwFatalError={err => Logger('newW4').error(err)}
          />
        </Paper>
      </Grid>
      <SubmitModal
        onClose={() => setIsConfirmationOpen(false)}
        onConfirm={confirmSubmission}
        open={isConfirmationOpen}
        country={country}
      />
    </Grid>
  );
};

export default withRouter(withStyles(styles)(NewW4));
